import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { configureClient } from '../api/client';
import { login as apiLogin, refreshToken as apiRefreshToken } from '../api/endpoints';
import type { User } from '../types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Token lives only in memory — not localStorage — to avoid XSS exposure.
// Refresh token is an httpOnly cookie set by the server.
const REFRESH_BUFFER_MS = 60_000; // refresh 60 s before expiry

function parseTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (typeof payload.exp === 'number') {
      return payload.exp * 1000;
    }
    return null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefresh = useCallback((token: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const expiry = parseTokenExpiry(token);
    if (!expiry) return;

    const delay = expiry - Date.now() - REFRESH_BUFFER_MS;
    if (delay <= 0) return;

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const response = await apiRefreshToken();
        setState({
          user: response.user,
          token: response.accessToken,
          isAuthenticated: true,
          isLoading: false,
        });
        scheduleRefresh(response.accessToken);
      } catch {
        setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
      }
    }, delay);
  }, []);

  // Configure the API client with current token getter and refresher
  useEffect(() => {
    configureClient(
      () => state.token,
      async () => {
        try {
          const response = await apiRefreshToken();
          setState({
            user: response.user,
            token: response.accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
          scheduleRefresh(response.accessToken);
          return response.accessToken;
        } catch {
          setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
          return null;
        }
      },
    );
  }, [state.token, scheduleRefresh]);

  // Attempt silent refresh on mount (uses httpOnly refresh cookie)
  useEffect(() => {
    apiRefreshToken()
      .then((response) => {
        setState({
          user: response.user,
          token: response.accessToken,
          isAuthenticated: true,
          isLoading: false,
        });
        scheduleRefresh(response.accessToken);
      })
      .catch(() => {
        setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
      });
  }, [scheduleRefresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await apiLogin(email, password);
      setState({
        user: response.user,
        token: response.accessToken,
        isAuthenticated: true,
        isLoading: false,
      });
      scheduleRefresh(response.accessToken);
    },
    [scheduleRefresh],
  );

  const logout = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
